import React, { useState, useEffect, useRef } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getTranslation } from '../translations';
import { quranAPI, speechAPI, progressAPI, settingsAPI, authAPI, realtimeAPI } from '../utils/api';
import { useRealtimeSpeechRecognition } from '../hooks/useRealtimeSpeechRecognition';
import { useChunkedSTT } from '../hooks/useChunkedSTT';
import { useRealtimeTracking } from '../hooks/useRealtimeTracking';
import { Mic, MicOff, ArrowLeft, Search, RotateCcw, Play, Star, Volume2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { PracticeModeSelector } from '../components/practice/PracticeModeSelector';
import { RealtimeStatusBar } from '../components/practice/RealtimeStatusBar';
import { SurahPicker } from '../components/practice/SurahPicker';
import { AyahDisplay } from '../components/practice/AyahDisplay';
import { AyahControls } from '../components/practice/AyahControls';

const Practice = () => {
  const { isKidsMode, language } = useMode();
  const navigate = useNavigate();
  const location = useLocation();
  const t = (key) => getTranslation(language, key);
  
  const [surahs, setSurahs] = useState([]);
  const [filteredSurahs, setFilteredSurahs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [selectedAyahFrom, setSelectedAyahFrom] = useState(1);
  const [selectedAyahTo, setSelectedAyahTo] = useState(1);
  const [currentAyah, setCurrentAyah] = useState(null);
  const [ayahData, setAyahData] = useState(null);
  const [lastPractice, setLastPractice] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [recentSurahs, setRecentSurahs] = useState([]);
  const [practiceMode, setPracticeMode] = useState('full'); // 'full', 'listen', 'read', 'mixed'
  const [viewMode, setViewMode] = useState('single'); // 'single' (ayet ayet) or 'page' (tüm sayfa)
  const [allAyahs, setAllAyahs] = useState([]); // For page view
  const [userPracticeMode, setUserPracticeMode] = useState('child'); // 'child' or 'adult'
  const [isRealtimeMode, setIsRealtimeMode] = useState(false); // true for adult mode
  const [user, setUser] = useState(null);
  
  // Detect mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  const [isRecording, setIsRecording] = useState(false);
  const [isPracticing, setIsPracticing] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [warningCount, setWarningCount] = useState(0);
  const [totalErrors, setTotalErrors] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const currentAudioRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Realtime tracking hooks
  const realtimeTracking = useRealtimeTracking();
  
  const handleRealtimeWord = async (word) => {
    console.log('📝 Received word from STT:', word);
    
    if (!realtimeTracking.isTracking) {
      console.warn('⚠️ Tracking not active, ignoring word:', word);
      return;
    }
    
    const result = await realtimeTracking.processWord(word);
    console.log('📊 Processing result:', result);
    
    if (result.success) {
      if (!result.is_correct) {
        // Error detected - IMMEDIATE feedback
        console.log(`❌ ERROR at position ${result.position}: expected "${result.expected_word}", got "${word}"`);
        
        if (result.error_count === 1) {
          // First error - play beep and show warning
          playBeep();
          toast.error(`❌ Hata! Beklenen: "${result.expected_word}"`, {
            duration: 2000,
            position: 'top-center'
          });
        } else if (result.should_intervene) {
          // Second error - speak correct word
          playCorrectWord(result.expected_word);
          toast.error(`🔊 Dinleyin: "${result.expected_word}"`, {
            duration: 3000,
            position: 'top-center'
          });
        }
      } else {
        // Correct word - visual feedback only
        console.log(`✅ CORRECT at position ${result.position}: "${word}"`);
        
        // Check if ayah is complete
        if (result.position >= result.total_words - 1 || result.progress_percentage >= 99) {
          console.log('🎉 Ayah completed! Moving to next...');
          toast.success('✓ Ayet tamamlandı! Bir sonrakine geçiliyor...', {
            duration: 2000
          });
          
          // Stop listening temporarily
          if (isMobile) {
            await streamingSTT.stopStreaming();
          } else {
            realtimeSpeech.stopListening();
          }
          setIsRecording(false);
          
          // Wait then move to next ayah
          setTimeout(() => {
            moveToNextAyah();
          }, 1500);
        }
      }
    } else {
      console.error('❌ Processing failed:', result.error);
    }
  };
  
  const moveToNextAyah = async () => {
    if (currentAyah < selectedAyahTo) {
      const nextAyah = currentAyah + 1;
      setCurrentAyah(nextAyah);
      await fetchAyah(nextAyah);
      
      // Restart tracking for new ayah
      const result = await realtimeTracking.startTracking(selectedSurah, nextAyah);
      if (result.success) {
        console.log('Started tracking next ayah:', nextAyah);
        toast.info(`Ayet ${nextAyah} - Devam edin!`);
      }
    } else {
      // Finished all ayahs
      toast.success('🎉 Tüm ayetler tamamlandı!');
      realtimeSpeech.stopListening();
      setIsRecording(false);
      setIsPracticing(false);
      
      // Show summary
      const summary = await realtimeTracking.getSummary();
      console.log('Practice summary:', summary);
    }
  };
  
  const realtimeSpeech = useRealtimeSpeechRecognition({
    onWord: handleRealtimeWord,
    onError: (error) => {
      console.error('Speech recognition error:', error);
      toast.error(error);
    },
    language: 'ar-SA',
    continuous: true
  });
  
  // Chunked STT (iOS Safari compatible — replaces WebSocket streaming).
  // Records audio in rolling ~5s windows via MediaRecorder, sends each chunk
  // to /api/speech/transcribe-chunk for near-real-time feedback.
  const ayahTextRef = useRef('');
  // Prevent double-advance from rapid overlapping chunks
  const autoAdvanceLockRef = useRef(false);
  useEffect(() => { ayahTextRef.current = ayahData?.text || ''; }, [ayahData]);

  const streamingSTT = useChunkedSTT({
    onTranscript: handleRealtimeWord,
    onChunk: (data) => {
      // Always show what Whisper heard so the user can debug pronunciation /
      // mic issues — even when the transcript is empty.
      if (!data) return;
      const text = (data.transcribed_text || '').trim();
      const errMsg = data.error;
      if (errMsg) {
        setFeedback(`⚠️ STT hatası: ${String(errMsg).slice(0, 80)}`);
      } else if (!text) {
        if (data.skipped) {
          setFeedback('🤫 Çok kısa/sessiz — daha yüksek sesle okuyun');
        } else {
          setFeedback('🎤 Algılanamadı — mikrofon izni ve ortam sesini kontrol edin');
        }
      } else {
        const score = Math.round((data.similarity_score || 0) * 100);
        setFeedback(`📝 "${text}" — Benzerlik: %${score}`);
        // AUTO-ADVANCE: high overall similarity → treat this ayah as completed
        // and move to the next one. Strict word-by-word matching is too brittle
        // for Quranic Arabic (Whisper sometimes drops/changes diacritics).
        if ((data.similarity_score || 0) >= 0.75 && !autoAdvanceLockRef.current) {
          autoAdvanceLockRef.current = true;
          toast.success(`Ayet ${currentAyah} tamamlandı! (%${score})`);
          setTimeout(() => {
            moveToNextAyah();
            autoAdvanceLockRef.current = false;
          }, 1200);
        }
      }
    },
    onError: (error) => {
      console.error('Chunked STT error:', error);
      toast.error(error);
    },
    chunkMs: 5000,
    getExpectedText: () => ayahTextRef.current,
  });

  useEffect(() => {
    fetchSurahs();
    fetchLastPractice();
    fetchFavorites();
    fetchRecentSurahs();
    fetchUserSettings();
    
    if (location.state?.continuePractice) {
      const { surahNumber, currentAyah } = location.state;
      setSelectedSurah(surahNumber);
      setSelectedAyahFrom(currentAyah || 1);
      setTimeout(() => {
        handleContinuePractice(surahNumber, currentAyah);
      }, 1000);
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const handleContinuePractice = async (surahNum, ayahNum) => {
    const surah = surahs.find(s => s.id === surahNum);
    if (surah) {
      setSelectedAyahTo(surah.total_verses);
      setIsPracticing(true);
      setCurrentAyah(ayahNum);
      await fetchAyah(ayahNum);
      toast.success(`Sure ${surahNum}, Ayet ${ayahNum}'den devam ediyorsunuz`);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredSurahs(surahs);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = surahs.filter(surah => 
        surah.transliteration.toLowerCase().includes(query) ||
        surah.translation.toLowerCase().includes(query) ||
        surah.name.includes(query) ||
        surah.id.toString().includes(query)
      );
      setFilteredSurahs(filtered);
    }
  }, [searchQuery, surahs]);

  const fetchSurahs = async () => {
    try {
      const response = await quranAPI.getSurahs();
      const surahsList = Array.isArray(response.data) ? response.data : [];
      setSurahs(surahsList);
      setFilteredSurahs(surahsList);
    } catch (error) {
      console.error('Sureler yükleme hatası:', error);
      toast.error('Sureler yüklenemedi');
      setSurahs([]);
      setFilteredSurahs([]);
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data;
      setUser(userData); // Set user state for streaming STT
      setUserPracticeMode(userData.practice_mode || 'child');
      setIsRealtimeMode(userData.practice_mode === 'adult');
      console.log('User practice mode:', userData.practice_mode);
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  };

  const playBeep = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // 800Hz beep
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.error('Error playing beep:', error);
    }
  };

  const playCorrectWord = async (arabicText) => {
    try {
      const response = await speechAPI.textToSpeech({ text: arabicText, language: 'ar' });
      const audioData = response.data.audio;
      
      const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
      currentAudioRef.current = audio;
      
      await audio.play();
      
      audio.onended = () => {
        currentAudioRef.current = null;
      };
    } catch (error) {
      console.error('Error playing correct word:', error);
      toast.error('Ses çalınamadı');
    }
  };

  const fetchLastPractice = async () => {
    try {
      const response = await progressAPI.getUserProgress();
      if (response.data && response.data.length > 0) {
        const sorted = response.data.sort((a, b) => 
          new Date(b.last_practiced) - new Date(a.last_practiced)
        );
        setLastPractice(sorted[0]);
      }
    } catch (error) {
      console.error('Son çalışma yüklenemedi:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await settingsAPI.getFavorites();
      setFavorites(response.data.favorites || []);
    } catch (error) {
      console.error('Favoriler yüklenemedi:', error);
    }
  };

  const fetchRecentSurahs = async () => {
    try {
      const response = await progressAPI.getUserProgress();
      if (response.data && response.data.length > 0) {
        const sorted = response.data.sort((a, b) => 
          new Date(b.last_practiced) - new Date(a.last_practiced)
        );
        const uniqueSurahs = [...new Set(sorted.map(p => p.surah_number))].slice(0, 5);
        setRecentSurahs(uniqueSurahs);
      }
    } catch (error) {
      console.error('Son sureler yüklenemedi:', error);
    }
  };

  const toggleFavorite = async (surahId) => {
    try {
      if (favorites.includes(surahId)) {
        await settingsAPI.removeFavorite(surahId);
        setFavorites(favorites.filter(id => id !== surahId));
        toast.success('Favorilerden çıkarıldı');
      } else {
        await settingsAPI.addFavorite(surahId);
        setFavorites([...favorites, surahId]);
        toast.success('Favorilere eklendi');
      }
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const loadLastPractice = () => {
    if (lastPractice) {
      setSelectedSurah(lastPractice.surah_number);
      setSelectedAyahFrom(lastPractice.ayah_from);
      setSelectedAyahTo(lastPractice.ayah_to);
      toast.success('Kaldığınız yerden devam ediyorsunuz');
    }
  };

  const selectSurah = (surah) => {
    setSelectedSurah(surah.id);
    setSearchQuery('');
    setSelectedAyahFrom(1);
    setSelectedAyahTo(surah.total_verses || 1);
  };

  const startPractice = async () => {
    if (!selectedSurah) {
      toast.error('Lütfen bir sure seçin');
      return;
    }
    
    setIsPracticing(true);
    setCurrentAyah(selectedAyahFrom);
    setErrorCount(0);
    setWarningCount(0);
    setTotalErrors(0);
    setFeedback('');
    
    await fetchAyah(selectedAyahFrom);
    
    // If page view mode, fetch all ayahs in the surah
    if (viewMode === 'page') {
      try {
        const response = await quranAPI.getAyahs(selectedSurah);
        setAllAyahs(response.data.verses || []);
      } catch (error) {
        console.error('Failed to fetch all ayahs:', error);
        toast.error('Ayetler yüklenemedi');
      }
    }
    
    // ADULT MODE: Start realtime tracking AND listening IMMEDIATELY
    if (isRealtimeMode && practiceMode === 'full') {
      const result = await realtimeTracking.startTracking(selectedSurah, selectedAyahFrom);
      if (result.success) {
        console.log('✅ Realtime tracking started:', result);
        
        // Start listening IMMEDIATELY based on device
        if (isMobile) {
          // Mobile: Use Streaming STT
          console.log('📱 Auto-starting Streaming STT for mobile');
          setIsRecording(true);
          await streamingSTT.startStreaming();
          toast.success('🎤 Gerçek zamanlı dinleme aktif - Okumaya başlayın!');
        } else if (realtimeSpeech.isSupported) {
          // Desktop: Use Web Speech API
          console.log('💻 Auto-starting Web Speech API for desktop');
          setIsRecording(true);
          realtimeSpeech.startListening();
          toast.success('🎤 Gerçek zamanlı dinleme aktif - Okumaya başlayın!');
        } else {
          toast.error('Tarayıcınız ses tanımayı desteklemiyor');
        }
      } else {
        toast.error('Takip başlatılamadı: ' + result.error);
      }
    }
  };

  const fetchAyah = async (ayahNumber) => {
    try {
      const response = await quranAPI.getAyah(selectedSurah, ayahNumber, language);
      setAyahData(response.data);
    } catch (error) {
      toast.error('Ayet yüklenemedi');
    }
  };

  const startRecording = async () => {
    // ADULT MODE: Use appropriate STT based on device
    if (isRealtimeMode && practiceMode === 'full') {
      // Start tracking if not already started
      if (!realtimeTracking.isTracking) {
        const result = await realtimeTracking.startTracking(selectedSurah, currentAyah);
        if (!result.success) {
          toast.error('Takip başlatılamadı: ' + result.error);
          return;
        }
        console.log('✅ Tracking started from manual microphone button');
      }
      
      // iOS/Mobile: Use streaming STT
      if (isMobile) {
        console.log('🎤 Starting STREAMING STT for mobile device');
        setIsRecording(true);
        await streamingSTT.startStreaming();
        toast.success('🎤 Gerçek zamanlı dinleme başladı!');
        return;
      }
      
      // Desktop: Use Web Speech API
      if (!realtimeSpeech.isSupported) {
        toast.error('Tarayıcınız gerçek zamanlı ses tanımayı desteklemiyor. Lütfen Chrome veya Edge kullanın.');
        return;
      }
      
      console.log('🎤 Starting REALTIME speech recognition (Web Speech API)');
      setIsRecording(true);
      realtimeSpeech.startListening();
      toast.success('🎤 Gerçek zamanlı dinleme başladı!');
      return; // EXIT - don't use MediaRecorder!
    }
    
    // CHILD MODE ONLY: Use MediaRecorder (old record-then-analyze system)
    console.log('📼 Child mode - using MediaRecorder');
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Tarayıcınız mikrofon erişimini desteklemiyor');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Audio level detection
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      
      const detectAudioLevel = () => {
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(Math.min(100, average));
        
        // Detect if user started speaking during TTS playback
        if (currentAudioRef.current && !currentAudioRef.current.paused && average > 30) {
          console.log('User started speaking, stopping TTS');
          currentAudioRef.current.pause();
          currentAudioRef.current = null;
          setFeedback('Devam edin...');
        }
        
        animationFrameRef.current = requestAnimationFrame(detectAudioLevel);
      };
      
      detectAudioLevel();
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendAudioForTranscription(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        setAudioLevel(0);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setFeedback(t('listening'));
      
      startSilenceTimer();
      
    } catch (error) {
      console.error('Recording error:', error);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Mikrofon izni reddedildi. Lütfen tarayıcı ayarlarından mikrofon iznini verin.', {
          duration: 5000
        });
        setFeedback('⚠️ Mikrofon izni gerekli. Tarayıcı ayarlarından izin verin.');
      } else if (error.name === 'NotFoundError') {
        toast.error('Mikrofon bulunamadı. Lütfen mikrofonunuzu kontrol edin.');
        setFeedback('⚠️ Mikrofon bulunamadı.');
      } else {
        toast.error('Mikrofon erişimi başarısız: ' + error.message);
        setFeedback('⚠️ Mikrofon hatası.');
      }
    }
  };

  const stopRecording = () => {
    // Stop realtime speech or streaming STT based on device
    if (isRealtimeMode) {
      if (isMobile && streamingSTT.isStreaming) {
        streamingSTT.stopStreaming();
      } else if (realtimeSpeech.isListening) {
        realtimeSpeech.stopListening();
      }
    }
    
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearTimeout(silenceTimerRef.current);
    }
  };

  const startSilenceTimer = () => {
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      if (isRecording) {
        handleWarning();
        stopRecording();
      }
    }, 5000);
  };

  const sendAudioForTranscription = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'audio.webm');
      formData.append('surah_number', selectedSurah);
      formData.append('ayah_number', currentAyah);

      const response = await speechAPI.transcribe(formData);
      const { is_correct, similarity_score, transcribed_text, expected_text } = response.data;

      if (is_correct) {
        setFeedback(t('correct'));
        setErrorCount(0);
        setWarningCount(0);
        
        setTimeout(() => {
          moveToNextAyah();
        }, 1500);
      } else {
        handleError(transcribed_text, expected_text);
      }
      
    } catch (error) {
      toast.error('Ses tanıma hatası');
      console.error('Transcription error:', error);
    }
  };

  const playWarningBeep = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const handleError = async (transcribed, expected) => {
    const newErrorCount = errorCount + 1;
    setErrorCount(newErrorCount);
    setTotalErrors(totalErrors + 1);
    
    if (newErrorCount === 1) {
      // First error: Play warning beep
      playWarningBeep();
      setFeedback(`⚠️ ${t('incorrect')} - Tekrar deneyin (1/2)`);
    } else if (newErrorCount >= 2) {
      // Second error: Play correct recitation
      setFeedback('Doğrusu okunuyor... (Okumaya başlarsanız ses duracak)');
      await playCorrectAudio(expected);
      
      setErrorCount(0);
      setTimeout(() => {
        moveToNextAyah();
      }, 1000);
    }
  };

  const handleWarning = () => {
    const newWarningCount = warningCount + 1;
    setWarningCount(newWarningCount);
    setTotalErrors(totalErrors + 1);
    
    if (newWarningCount >= 3) {
      setFeedback('Çok fazla duraksama. Doğrusu okunuyor...');
      playCorrectAudio(ayahData?.text);
      setWarningCount(0);
    } else {
      setFeedback(`Çok yavaş okudunuz (${newWarningCount}/3)`);
    }
  };

  const playCorrectAudio = async (text) => {
    try {
      const response = await speechAPI.textToSpeech(text, 'ar');
      const audioBase64 = response.data.audio;
      
      const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
      currentAudioRef.current = audio;
      
      audio.play();
      
      await new Promise(resolve => {
        audio.onended = () => {
          currentAudioRef.current = null;
          resolve();
        };
      });
      
    } catch (error) {
      console.error('TTS error:', error);
      currentAudioRef.current = null;
    }
  };

  const saveProgress = async () => {
    try {
      await progressAPI.updateProgress({
        surah_number: selectedSurah,
        ayah_from: selectedAyahFrom,
        ayah_to: currentAyah,
        is_successful: totalErrors < 3,
        errors: totalErrors
      });
    } catch (error) {
      console.error('Progress save error:', error);
    }
  };

  const getSelectedSurahInfo = () => {
    return surahs.find(s => s.id === selectedSurah);
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)'
      }}
    >
      <header className="px-6 py-4 bg-white/50 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1
            className={`text-2xl font-bold ${
              isKidsMode ? 'font-kids text-green-500' : 'font-elegant text-emerald-900'
            }`}
          >
            {t('practice')}
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {!isPracticing ? (
          <div className="space-y-6">
            {/* Practice Mode Selection */}
            <PracticeModeSelector
              isKidsMode={isKidsMode}
              practiceMode={practiceMode}
              setPracticeMode={setPracticeMode}
            />

            {/* View Mode Toggle (Ayet/Sayfa) */}
            <div
              className={`p-6 ${
                isKidsMode
                  ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                  : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
              }`}
            >
              <h3 className="font-bold mb-3 text-lg">📚 Görünüm Modu</h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setViewMode('single')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    viewMode === 'single'
                      ? isKidsMode
                        ? 'border-green-400 bg-green-50'
                        : 'border-emerald-700 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${isKidsMode ? 'rounded-2xl' : ''}`}
                >
                  <div className="text-3xl mb-2">📖</div>
                  <div className="font-bold mb-1">Ayet Ayet</div>
                  <div className="text-xs text-gray-600">Tek tek göster</div>
                </button>
                <button
                  onClick={() => setViewMode('page')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    viewMode === 'page'
                      ? isKidsMode
                        ? 'border-green-400 bg-green-50'
                        : 'border-emerald-700 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${isKidsMode ? 'rounded-2xl' : ''}`}
                >
                  <div className="text-3xl mb-2">📄</div>
                  <div className="font-bold mb-1">Tüm Sayfa</div>
                  <div className="text-xs text-gray-600">Hepsini göster</div>
                </button>
              </div>
            </div>

            {/* Favorites & Recent */}
            {(favorites.length > 0 || recentSurahs.length > 0) && (
              <div
                className={`p-6 ${
                  isKidsMode
                    ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                    : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
                }`}
              >
                {favorites.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      <h3 className="font-bold">Favoriler</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {favorites.map((surahId) => {
                        const surah = surahs.find(s => s.id === surahId);
                        return surah ? (
                          <button
                            key={surahId}
                            onClick={() => selectSurah(surah)}
                            className={`px-4 py-2 rounded-lg border-2 transition-all ${
                              selectedSurah === surahId
                                ? 'border-yellow-400 bg-yellow-50'
                                : 'border-gray-200 hover:border-yellow-300'
                            }`}
                          >
                            {surah.id}. {surah.transliteration}
                          </button>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
                
                {recentSurahs.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-blue-500" />
                      <h3 className="font-bold">Son Çalışılanlar</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSurahs.map((surahId) => {
                        const surah = surahs.find(s => s.id === surahId);
                        return surah ? (
                          <button
                            key={surahId}
                            onClick={() => selectSurah(surah)}
                            className={`px-4 py-2 rounded-lg border-2 transition-all ${
                              selectedSurah === surahId
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            {surah.id}. {surah.transliteration}
                          </button>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {lastPractice && (
              <div
                className={`p-6 ${
                  isKidsMode
                    ? 'bg-yellow-100 border-2 border-yellow-300 rounded-3xl'
                    : 'bg-amber-50 border border-amber-200 rounded-xl'
                }`}
                data-testid="last-practice-card"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <RotateCcw className="w-5 h-5 text-amber-600" />
                      <h3 className={`font-bold ${isKidsMode ? 'font-kids text-lg' : 'text-base'}`}>
                        Kaldığın Yerden Devam Et
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      Sure {lastPractice.surah_number}, Ayet {lastPractice.ayah_from}-{lastPractice.ayah_to}
                    </p>
                  </div>
                  <button
                    onClick={loadLastPractice}
                    className={`px-6 py-3 font-bold text-white transition-all ${
                      isKidsMode
                        ? 'bg-amber-400 rounded-full hover:bg-amber-500'
                        : 'bg-amber-600 rounded-md hover:bg-amber-700'
                    }`}
                    data-testid="continue-button"
                  >
                    Devam Et
                  </button>
                </div>
              </div>
            )}

            <SurahPicker
              isKidsMode={isKidsMode}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filteredSurahs={filteredSurahs}
              selectedSurah={selectedSurah}
              favorites={favorites}
              selectSurah={selectSurah}
              toggleFavorite={toggleFavorite}
              startPractice={startPractice}
              getSelectedSurahInfo={getSelectedSurahInfo}
            />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Realtime Status Indicator */}
            {isRealtimeMode && realtimeTracking.isTracking && (
              <RealtimeStatusBar
                isKidsMode={isKidsMode}
                isMobile={isMobile}
                isActive={isMobile ? streamingSTT.isStreaming : realtimeSpeech.isListening}
                chunkMs={5000}
                currentWordIndex={realtimeTracking.currentWordIndex}
                errorsCount={realtimeTracking.errors.length}
                lastResult={realtimeTracking.lastResult}
              />
            )}

            {/* Ayah Display */}
            <AyahDisplay
              isKidsMode={isKidsMode}
              viewMode={viewMode}
              currentAyah={currentAyah}
              selectedAyahTo={selectedAyahTo}
              ayahData={ayahData}
              isRealtimeMode={isRealtimeMode}
              realtimeTracking={realtimeTracking}
              allAyahs={allAyahs}
            />

            <AyahControls
              isKidsMode={isKidsMode}
              practiceMode={practiceMode}
              isRealtimeMode={isRealtimeMode}
              isRecording={isRecording}
              startRecording={startRecording}
              stopRecording={stopRecording}
              audioLevel={audioLevel}
              feedback={feedback}
              errorCount={errorCount}
              totalErrors={totalErrors}
              correctLabel={t('correct')}
            />

            <button
              onClick={moveToNextAyah}
              className={`w-full py-3 font-medium text-white transition-all ${
                isKidsMode
                  ? 'bg-yellow-400 rounded-full hover:bg-yellow-500'
                  : 'bg-amber-600 rounded-md hover:bg-amber-700'
              }`}
              data-testid="continue-button"
            >
              {t('continue')} (Sonraki Ayet)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Practice;
